import { useState, useCallback, useRef, useEffect } from 'react';

const STORAGE_KEY = 'ai-assist-panel-width';
const MIN_PANEL_WIDTH = 340;
const MAX_PANEL_WIDTH_RATIO = 0.5;
const MIN_CONTENT_WIDTH = 560;

/** Returns the width of the course content wrapper (excludes open side panels). */
function getContentWrapperWidth(): number {
  const wrapper = document.querySelector<HTMLElement>(
    '.hud-course-view__content-wrapper',
  );
  return wrapper ? wrapper.offsetWidth : window.innerWidth;
}

/** Returns the maximum allowed panel width, respecting both the 50% viewport
 *  cap and the minimum content area needed for the HUD icons. */
function getMaxWidth(): number {
  const halfWidth = Math.floor(window.innerWidth * MAX_PANEL_WIDTH_RATIO);
  const contentConstrainedMax = getContentWrapperWidth() - MIN_CONTENT_WIDTH;
  return Math.max(MIN_PANEL_WIDTH, Math.min(halfWidth, contentConstrainedMax));
}

function getStoredWidth(): number {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (!Number.isNaN(parsed) && parsed >= MIN_PANEL_WIDTH) {
        return parsed;
      }
    }
  } catch {
    // localStorage unavailable (e.g. private browsing restrictions)
  }
  return MIN_PANEL_WIDTH;
}

type UseAiAssistPanelResizeReturn = {
  panelWidth: number;
  isDragging: boolean;
  onDragHandleMouseDown: (e: React.MouseEvent) => void;
};

type UseAiAssistPanelResizeOptions = {
  /** URL of the SVG to use as cursor during drag. Should match the CSS cursor on
   *  the drag handle so the cursor stays consistent as the mouse moves off the handle. */
  dragCursorUrl?: string;
};

export default function useAiAssistPanelResize({
  dragCursorUrl,
}: UseAiAssistPanelResizeOptions = {}): UseAiAssistPanelResizeReturn {
  const [panelWidth, setPanelWidth] = useState<number>(getStoredWidth);
  const [isDragging, setIsDragging] = useState(false);

  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const currentWidthRef = useRef(panelWidth);

  useEffect(() => {
    currentWidthRef.current = panelWidth;
  }, [panelWidth]);

  useEffect(() => {
    setPanelWidth((prev) => Math.min(prev, getMaxWidth()));
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setPanelWidth((prev) => Math.min(prev, getMaxWidth()));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const onMouseMove = useCallback((e: MouseEvent) => {
    if (!isDraggingRef.current) return;
    // Panel is on the right; dragging handle left (negative clientX delta) expands.
    const delta = startXRef.current - e.clientX;
    const newWidth = Math.min(
      Math.max(startWidthRef.current + delta, MIN_PANEL_WIDTH),
      getMaxWidth(),
    );
    currentWidthRef.current = newWidth;
    setPanelWidth(newWidth);
  }, []);

  const onMouseUp = useCallback(() => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    setIsDragging(false);
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
    document.body.style.removeProperty('user-select');
    document.body.style.removeProperty('cursor');
    document.body.classList.remove('ai-panel-resizing');
    try {
      localStorage.setItem(STORAGE_KEY, String(currentWidthRef.current));
    } catch {
      // localStorage unavailable
    }
  }, [onMouseMove]);

  const onDragHandleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      isDraggingRef.current = true;
      setIsDragging(true);
      startXRef.current = e.clientX;
      // Clamp the start width so that if a stale stored value exceeds the current
      // max, the delta calculation behaves correctly from the very first move.
      startWidthRef.current = Math.min(currentWidthRef.current, getMaxWidth());
      document.body.style.userSelect = 'none';
      // Match the CSS cursor on the drag handle so it stays consistent while
      // the mouse moves across the page during drag.
      document.body.style.cursor = dragCursorUrl
        ? `url(${dragCursorUrl}) 12 12, ew-resize`
        : 'ew-resize';
      // Suppress the content-wrapper padding transition during drag.
      document.body.classList.add('ai-panel-resizing');
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [onMouseMove, onMouseUp],
  );

  useEffect(
    () => () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.classList.remove('ai-panel-resizing');
    },
    [onMouseMove, onMouseUp],
  );

  return { panelWidth, isDragging, onDragHandleMouseDown };
}
